import { ArenaTeamSelectCard } from "./arena-team-select-card";
import { useEffect, useState, useCallback } from "react";
import { RouteNames } from "@/types/route-names";
import { useSPAContext } from "@/providers/spa-provider";
import { useBackAction } from "@/hooks/use-back-action";
import { listPokeTeams } from "@/services/poketeam/list-poketeams";
import { useAuth } from "@/providers/auth-provider";
import { Alert } from "@/components/alert/alert";
import { PokeTeam } from "@/types";
import { useDuel } from "@/providers/duel-provider";

/**
 * Arena Team Selection View - Select a team before entering the arena queue
 */
export const ArenaTeamSelectView = () => {
  const { setCurrentRoute, previousRoute } = useSPAContext();
  const { getAccessToken, state: authState } = useAuth();
  const { enqueue } = useDuel();

  const handleBack = useCallback(() => {
    setCurrentRoute(previousRoute);
  }, [previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const [teams, setTeams] = useState<PokeTeam[]>();
  const [isEnqueuing, setIsEnqueuing] = useState(false);

  const getTeams = useCallback(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }

    const response = await listPokeTeams(accessToken);

    if (response.ok && response.data?.teams) {
      setTeams(response.data.teams);
    } else {
      Alert("Failed to load teams");
    }
  }, [getAccessToken]);

  const handleTeamSelect = useCallback(
    async (team: PokeTeam) => {
      if (isEnqueuing) return;

      if (team.pokemons.length < 8) {
        Alert("This team needs at least 8 Pokémon to enter the arena");
        return;
      }

      const accessToken = getAccessToken();
      if (!accessToken || authState.status !== "authenticated") {
        Alert("Authentication error. Please try again");
        return;
      }

      const playerId = authState.user?.telegram_id;
      if (!playerId) {
        Alert("Authentication error. Please try again");
        return;
      }

      setIsEnqueuing(true);
      try {
        setCurrentRoute(RouteNames.DUEL_QUEUE);
        await enqueue(
          accessToken,
          playerId,
          "RANKED_1V1",
          team.id,
          team.pokemons,
        );
      } catch (error) {
        console.error("Failed to start duel:", error);
        Alert("Failed to start duel");
      } finally {
        setIsEnqueuing(false);
      }
    },
    [isEnqueuing, getAccessToken, authState, setCurrentRoute, enqueue],
  );

  useEffect(() => {
    void getTeams();
  }, [getTeams]);

  return (
    <div className="flex flex-col h-full w-full relative overflow-y-auto bg-pk-night-1/90 backdrop-blur-md pb-24">
      {/* Header - aligned with play-menu */}
      <div className="relative z-10 flex items-center justify-between mt-6 xs:mt-8">
        <div className="flex flex-col ml-4 p-2">
          <div className="relative">
            <h1 className="text-3xl xs:text-4xl font-black text-white uppercase tracking-tight leading-none drop-shadow-lg">
              SELECT ARENA TEAM
            </h1>
            <div className="h-1 w-12 xs:w-16 bg-pk-yellow-1 mt-1 skew-x-[-20deg]" />
          </div>
        </div>
      </div>

      {/* Content - aligned with play-menu + inventory frame */}
      <div className="flex flex-col gap-4 xs:gap-6 px-4 pt-4 xs:pt-6 relative z-10 mx-auto w-full max-w-md">
        {/* Teams container - inventory-style frame */}
        <div
          className="border-4 border-gray-900 relative flex flex-col overflow-hidden rounded-lg shadow-cartoon-sm min-h-[200px] max-h-[60vh]"
          style={{ backgroundColor: "#3b2c35" }}
        >
          <div className="absolute bg-black/70 inset-0 border-4 border-white/20 pointer-events-none z-10 rounded-lg" />
          <div
            className="absolute inset-0 z-0 pointer-events-none rounded-lg"
            style={{
              backgroundImage: "url(/assets/app-ui/glass-2.png)",
              backgroundSize: "cover",
              backgroundPosition: "left",
              backgroundRepeat: "repeat",
              mixBlendMode: "multiply",
              opacity: 0.35,
            }}
          />
          {!teams && (
            <div className="flex flex-col items-center justify-center py-12 animate-pulse relative z-20">
              <span className="text-white font-semibold text-lg">
                Loading teams...
              </span>
              <p className="text-pk-gray-4 text-xs mt-2">
                This should only take a moment
              </p>
            </div>
          )}
          {teams && teams.length > 0 && (
            <TeamList
              teams={teams}
              handleTeamClick={handleTeamSelect}
              isEnqueuing={isEnqueuing}
            />
          )}
          {teams && teams.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center relative z-20">
              <span className="text-white font-semibold text-lg">
                No teams yet
              </span>
              <p className="text-pk-gray-4 text-sm mt-2 max-w-sm">
                Create a team with 8 Pokémon to play in the arena.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TeamList = ({
  teams,
  handleTeamClick,
  isEnqueuing,
}: {
  teams: PokeTeam[];
  handleTeamClick: (team: PokeTeam) => void;
  isEnqueuing: boolean;
}) => {
  return (
    <div className="h-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-2 relative z-20 flex flex-col gap-3">
      {teams?.map((team) => {
        const isBlocked = team.pokemons.length < 8;
        return (
          <div
            key={team.id}
            className={`relative ${
              isEnqueuing ? "opacity-50 pointer-events-none" : ""
            } ${isBlocked ? "pointer-events-none" : ""}`}
          >
            {isBlocked && (
              <div className="absolute inset-0 z-30 flex items-center justify-center rounded-lg bg-black/60 backdrop-blur-[2px]">
                <span className="text-white font-bold text-sm uppercase tracking-wide drop-shadow-md">
                  Need 8 Pokémon
                </span>
              </div>
            )}
            <div className={isBlocked ? "opacity-60" : ""}>
              <ArenaTeamSelectCard
                team={team}
                {...(!isBlocked && { onClick: handleTeamClick })}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
